import dayjs from "dayjs"
import React, { useState } from "react"
import { twMerge } from "tailwind-merge"

const BookingSection = ({ bookableEvents, bookableEventSeries, practice, theme }) => {
  const [showAll, setShowAll] = useState(false)

  // Support both v1 (colorAccent) and v2 (colors.accent) theme structures
  // Use primary color for buttons and accent elements
  const accentColor =
    theme?.colors?.primary || theme?.colorPrimary || theme?.colors?.accent || theme?.colorAccent || "#F5A623"
  const buttonBackgroundColor = theme?.colors?.primary || theme?.colorPrimary || "#BD654F"
  const textDarkColor = theme?.colors?.textDark || theme?.colorTextDark || "#55433F"
  const textLightColor = theme?.colors?.textLight || theme?.colorTextLight || "#FFFFFF"
  const greyLightestColor = theme?.colors?.greyLightest || theme?.colorGreyLightest || "#F8F8F8"
  const whiteColor = theme?.colors?.white || theme?.colorWhite || "#FFFFFF"
  const greyLightColor = theme?.colors?.greyLight || theme?.colorGreyLight || "#E0E0E0"
  const headingFont = theme?.fonts?.heading || theme?.fontHeading || "Playfair Display, serif"
  const paragraphFont = theme?.fonts?.paragraph || theme?.fontParagraph || "Work Sans, sans-serif"

  // Helper to get border radius classes based on theme
  const getBorderRadiusClasses = () => {
    if (!theme?.buttonStyle) return "rounded-xl"
    switch (theme.buttonStyle) {
      case "square":
        return "rounded-none"
      case "rounded":
        return "rounded-xl"
      case "pill":
        return "rounded-full"
      default:
        return "rounded-xl"
    }
  }

  // Helper to get button border radius based on theme
  const getButtonBorderRadius = () => {
    if (!theme?.buttonStyle) return "12px"
    switch (theme.buttonStyle) {
      case "square":
        return "0px"
      case "rounded":
        return "12px"
      case "pill":
        return "32px"
      default:
        return "12px"
    }
  }

  // Utils to compute readable text color against background
  const hexToRgb = (hex) => {
    if (!hex) return { r: 255, g: 255, b: 255 }
    const cleaned = hex.replace("#", "")
    const full =
      cleaned.length === 3
        ? cleaned
            .split("")
            .map((c) => c + c)
            .join("")
        : cleaned
    const bigint = parseInt(full, 16)
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 }
  }

  const relativeLuminance = ({ r, g, b }) => {
    const srgb = [r, g, b].map((v) => {
      const c = v / 255
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2]
  }

  const getReadableTextColor = (bgHex, lightFallback = "#FFFFFF", darkFallback = "#000000") => {
    const lum = relativeLuminance(hexToRgb(bgHex))
    return lum > 0.6 ? darkFallback : lightFallback
  }

  // Sort events by start time
  const sortedEvents = bookableEvents?.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt)) || []

  // Determine how many events to show
  const eventsToShow = showAll ? sortedEvents : sortedEvents.slice(0, 6)
  const shouldShowToggle = sortedEvents.length > 6

  const handleBooking = (event) => {
    const practiceId = practice.id
    window.location.href = `/checkout?pid=${practiceId}&beid=${event.id}`
  }

  const handleProgramBooking = (series) => {
    const practiceId = practice.id
    window.location.href = `/checkout?pid=${practiceId}&bseid=${series.id}`
  }

  const toggleShowAll = () => {
    setShowAll(!showAll)
  }

  const formatDateBlock = (date) => {
    const dayObj = dayjs(date)
    return {
      month: dayObj.format("MMM").toUpperCase(),
      day: dayObj.format("D")
    }
  }

  const formatEventDetails = (event, isMobile = false) => {
    const dayObj = dayjs(event.startsAt)
    const dateFormat = isMobile ? "ddd, MMM D, YYYY" : "dddd, MMM D, YYYY"
    const formattedDate = dayObj.format(dateFormat)

    if (event.allDay) {
      return {
        date: formattedDate,
        time: "All day"
      }
    }

    const startTime = dayObj.format("h:mma").toLowerCase()
    const endTime = dayjs(event.endsAt).format("h:mma").toLowerCase()
    return {
      date: formattedDate,
      time: `${startTime} - ${endTime}`
    }
  }

  if (!bookableEvents?.length && !bookableEventSeries?.length) {
    return (
      <div className="mt-12">
        <div className="rounded-lg border border-gray-light bg-gray-ultralight p-6 text-center">
          <p className="text-gray">No available events at this time.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-12">
      <h3
        className={twMerge("mb-6 text-2xl font-bold", getBorderRadiusClasses())}
        style={{
          color: `${textDarkColor} !important`,
          fontFamily: headingFont,
          fontWeight: theme?.fonts?.headingWeight || "700"
        }}>
        Book Now
      </h3>

      <div className="space-y-4">
        {bookableEventSeries?.map((series) => (
          <div
            key={`series-${series.id}`}
            className={twMerge("flex items-center justify-between p-6", getBorderRadiusClasses())}
            style={{ backgroundColor: greyLightestColor }}>
            <div className="flex flex-col gap-1">
              <div
                className="text-lg font-semibold leading-normal"
                style={{ color: `${textDarkColor} !important`, fontFamily: paragraphFont }}>
                {series.sessionCount}-session program
              </div>
              <div
                className="text-base font-normal leading-normal"
                style={{ color: `${textDarkColor} !important`, fontFamily: paragraphFont }}>
                {series.formattedScheduleSummary}
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-2">
              <button
                onClick={() => handleProgramBooking(series)}
                className={twMerge(
                  "px-10 py-3 font-medium leading-normal transition-colors hover:opacity-90",
                  getBorderRadiusClasses()
                )}
                style={{
                  backgroundColor: buttonBackgroundColor,
                  color: getReadableTextColor(buttonBackgroundColor, textLightColor, textDarkColor),
                  fontFamily: paragraphFont,
                  borderRadius: theme?.borderRadius || getButtonBorderRadius(),
                  border: "none"
                }}>
                Enroll
              </button>
              {series.spotsAvailable > 0 && (
                <div
                  className="text-center text-sm font-normal leading-tight"
                  style={{ color: `${accentColor} !important`, fontFamily: paragraphFont }}>
                  {series.spotsAvailable} {series.spotsAvailable === 1 ? "spot" : "spots"} left
                </div>
              )}
            </div>
          </div>
        ))}

        {eventsToShow.map((event) => {
          const dateBlock = formatDateBlock(event.startsAt)
          const eventDetails = formatEventDetails(event)

          return (
            <div
              key={event.id}
              className={twMerge("flex items-center justify-between p-6", getBorderRadiusClasses())}
              style={{ backgroundColor: greyLightestColor }}>
              {/* Desktop Layout */}
              <div className="flex items-center gap-4">
                {/* Date Block */}
                <div
                  className={twMerge(
                    "flex h-20 min-h-20 min-w-16 flex-col items-center justify-center gap-0.5 border px-4 py-3 md:hidden",
                    getBorderRadiusClasses()
                  )}
                  style={{ backgroundColor: whiteColor, borderColor: greyLightColor }}>
                  <div
                    className="text-center text-sm font-semibold leading-tight"
                    style={{
                      color: `${textDarkColor} !important`,
                      fontFamily: paragraphFont
                    }}>
                    {dateBlock.month}
                  </div>
                  <div
                    className="text-center text-3xl font-semibold leading-10"
                    style={{
                      color: `${textDarkColor} !important`,
                      fontFamily: paragraphFont
                    }}>
                    {dateBlock.day}
                  </div>
                </div>

                {/* Event Details */}
                <div className="flex flex-col gap-1">
                  <div
                    className="text-lg font-semibold leading-normal"
                    style={{
                      color: `${textDarkColor} !important`,
                      fontFamily: paragraphFont
                    }}>
                    {eventDetails.date}
                  </div>
                  <div
                    className="text-base font-normal leading-normal"
                    style={{
                      color: `${textDarkColor} !important`,
                      fontFamily: paragraphFont
                    }}>
                    {eventDetails.time}
                  </div>
                </div>
              </div>

              {/* Booking Button and Spots */}
              <div className="flex flex-col items-center justify-center gap-2">
                <button
                  onClick={() => handleBooking(event)}
                  className={twMerge(
                    "px-10 py-3 font-medium leading-normal transition-colors hover:opacity-90",
                    getBorderRadiusClasses()
                  )}
                  style={{
                    backgroundColor: buttonBackgroundColor,
                    color: getReadableTextColor(buttonBackgroundColor, textLightColor, textDarkColor),
                    fontFamily: paragraphFont,
                    borderRadius: theme?.borderRadius || getButtonBorderRadius(),
                    border: "none",
                    outline: "none",
                    boxShadow: "none"
                  }}
                  ref={(el) => {
                    if (el) {
                      el.style.setProperty("background-color", buttonBackgroundColor, "important")
                      el.style.setProperty(
                        "color",
                        getReadableTextColor(buttonBackgroundColor, textLightColor, textDarkColor),
                        "important"
                      )
                    }
                  }}>
                  Book
                </button>
                {event.spotsAvailable > 0 && (
                  <div
                    className="text-center text-sm font-normal leading-tight"
                    style={{
                      color: `${accentColor} !important`,
                      fontFamily: paragraphFont
                    }}>
                    {event.spotsAvailable} {event.spotsAvailable === 1 ? "spot" : "spots"} left
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Show More/Less Toggle */}
      {shouldShowToggle && (
        <div className="mt-6">
          <button
            onClick={toggleShowAll}
            className="text-sm font-semibold underline transition-colors duration-150 hover:opacity-75"
            style={{
              color: `${textDarkColor} !important`,
              fontFamily: paragraphFont
            }}>
            {showAll
              ? "Show less"
              : `Show more (${sortedEvents.length - 6} more event${sortedEvents.length - 6 === 1 ? "" : "s"})`}
          </button>
        </div>
      )}
    </div>
  )
}

export default BookingSection
